import en from '../locales/en.json';
import es from '../locales/es.json';
import ht from '../locales/ht.json';

export const SUPPORTED_LANGUAGES = [
  {
    code: 'en',
    label: 'English',
    shortLabel: 'EN',
    flag: '/images/flags/en.svg',
    profileAliases: ['english', 'en'],
    translation: en,
  },
  {
    code: 'es',
    label: 'Spanish',
    shortLabel: 'ES',
    flag: '/images/flags/es.svg',
    profileAliases: ['spanish', 'es', 'espanol', 'español'],
    translation: es,
  },
  {
    code: 'ht',
    label: 'Kreyol',
    shortLabel: 'HT',
    flag: '/images/flags/ht.svg',
    profileAliases: ['creole', 'haitian creole', 'haitian', 'kreyol', 'kreyòl', 'ht'],
    translation: ht,
  },
] as const;

export type PortalLocale = typeof SUPPORTED_LANGUAGES[number]['code'];

export const DEFAULT_PORTAL_LOCALE: PortalLocale = 'en';

const SUPPORTED_LANGUAGE_CODES = new Set<string>(
  SUPPORTED_LANGUAGES.map((language) => language.code)
);

const PROFILE_LANGUAGE_TO_LOCALE = new Map<string, PortalLocale>(
  SUPPORTED_LANGUAGES.flatMap((language) => (
    language.profileAliases.map((alias) => [alias, language.code] as const)
  ))
);

export function isPortalLocale(value: unknown): value is PortalLocale {
  return typeof value === 'string' && SUPPORTED_LANGUAGE_CODES.has(value);
}

export function normalizePortalLocale(value: unknown): PortalLocale {
  const normalized = String(value || '').trim().toLowerCase();
  if (isPortalLocale(normalized)) return normalized;
  return PROFILE_LANGUAGE_TO_LOCALE.get(normalized) ?? DEFAULT_PORTAL_LOCALE;
}

export function localeFromProfileLanguage(value: unknown): PortalLocale {
  return normalizePortalLocale(value);
}

export function languageByLocale(locale: unknown) {
  const normalizedLocale = normalizePortalLocale(locale);
  return SUPPORTED_LANGUAGES.find((language) => language.code === normalizedLocale)
    ?? SUPPORTED_LANGUAGES[0];
}

export function i18nResources() {
  return Object.fromEntries(
    SUPPORTED_LANGUAGES.map((language) => [
      language.code,
      { translation: language.translation },
    ])
  );
}
